import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { tableCellClasses } from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CustomPagination from '../../common/components/CustomPagination';
import DataTable from '../../common/components/DataTable';
import { DefaultPageSize } from '../../common/utils/commonUtils';
import { TimeTrackerResponseItem, TimeTrackerSearchResponse } from '../../types/TimeTracker';
import ContextMenu from '../patient/ContextMenu';
import EditInterviewIcon from '../../common/components/icons/EditInterviewIcon';
import { SortDirs } from '../../common/utils/sortInfo';
import DeleteReportIcon from '../../common/components/icons/DeleteReportIcon';
import styled from '@emotion/styled';
import { getNumberInUSFormat } from '../../common/utils/patientUtils';

interface TimeTrackerResultProps {
  timeTrackerData?: TimeTrackerSearchResponse;
  triggerTimeTrackerQuery: (params: any) => Promise<void>;
  editTimeTracker: (timeTracker: TimeTrackerResponseItem) => Promise<void>;
  deleteTimeTracker: (id: number) => void;
}

const StyledDataCell = styled('p')(() => ({
  margin: 0,
  fontSize: '12px',
}));

export default function TimeTrackerResult({ timeTrackerData, triggerTimeTrackerQuery, editTimeTracker, deleteTimeTracker }: TimeTrackerResultProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const { timeTrackers, totalTimeTrackers, totalMinutes } = timeTrackerData || {};
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const searchOptions = {
    patientId: searchParams.get('patientId'),
    fromDate: searchParams.get('fromDate'),
    toDate: searchParams.get('toDate'),
    page: searchParams.get('page') || '1',
    pageSize: searchParams.get('pageSize') || DefaultPageSize,
    sort: searchParams.get('sort') || 'dateOfService',
    dir: searchParams.get('dir') || 'desc',
  };

  const showPagingInfo = totalPages > 1;
  const currentPage = parseInt(searchOptions.page);
  const handlePageChange = (value: any) => {
    const newPage = parseInt(value);
    updateSearchUrl({
      page: newPage,
    });
  };

  useEffect(() => {
    if (totalTimeTrackers) {
      setTotalPages(Math.ceil(Number(totalTimeTrackers) / Number(searchOptions.pageSize)));
    }
  }, [totalTimeTrackers, searchOptions.pageSize]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const updateSearchUrl = (updatedData: any) => {
    const params = {
      ...searchOptions,
      ...updatedData,
    };
    if (triggerTimeTrackerQuery) {
      triggerTimeTrackerQuery(params);
    }
  };

  const handleTableSorting = (sortField: string) => {
    if (sortField === searchOptions.sort) {
      const newDir = searchOptions.dir === SortDirs.Asc ? SortDirs.Desc : SortDirs.Asc;
      updateSearchUrl({ dir: newDir });
    } else {
      updateSearchUrl({
        sort: sortField,
        dir: SortDirs.Desc,
      });
    }
  };

  const renderContextMenu = (item: TimeTrackerResponseItem) => {
    return (
      <ContextMenu
        id={`time-tracker-${item.id}-menu`}
        anchorEl={anchorEl}
        handleClick={handleClick}
        handleClose={handleClose}
        menuItems={[
          {
            isDisplayed: true,
            label: t('timeTracker.edit'),
            icon: <EditInterviewIcon />,
            onClick: () => {
              setAnchorEl(null);
              editTimeTracker(item);
            },
          },
          {
            isDisplayed: true,
            label: t('timeTracker.delete'),
            icon: <DeleteReportIcon />,
            onClick: () => {
              setAnchorEl(null);
              deleteTimeTracker(item.id);
            },
          },
        ]}
      />
    );
  };

  const getRowData = (item: TimeTrackerResponseItem) => {
    return [
      { component: <StyledDataCell>{item.dateOfService}</StyledDataCell> },
      { component: <StyledDataCell>{item.externalId}</StyledDataCell> },
      { component: <StyledDataCell>{item.patientName}</StyledDataCell> },
      { component: <StyledDataCell>{item.careTeamMember}</StyledDataCell> },
      { component: <StyledDataCell>{item.role}</StyledDataCell> },
      { component: <StyledDataCell>{item.typeOfService}</StyledDataCell> },
      { component: <StyledDataCell>{getNumberInUSFormat(item.minutes ?? 0)}</StyledDataCell> },
      { component: <StyledDataCell>{item.cptCode}</StyledDataCell> },
      { component: renderContextMenu(item), },
    ];
  };

  useEffect(() => {
    if (timeTrackers && !timeTrackers.length) {
      const noDataFoundError = t('timeTracker.error.noDataFound');
      if (pageErrors[0] !== noDataFoundError) {
        setPageErrors([noDataFoundError]);
      }
    } else if (pageErrors.length > 0) {
      setPageErrors([]);
    }
  }, [timeTrackers, t, pageErrors]);

  const renderDesktopData = (data: TimeTrackerResponseItem[]) => {
    return (
      <DataTable
        sort={{ field: searchOptions.sort, dir: searchOptions.dir }}
        onSorted={handleTableSorting}
        commonCellStyles={{
          [`&.${tableCellClasses.head}`]: {
            padding: '16px 8px',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
            fontSize: '12px',
          },
          [`&.${tableCellClasses.body}`]: {
            padding: '16px 8px',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
          },
          [`&.${tableCellClasses.body}:first-of-type`]: {
            maxWidth: 'none',
            wordBreak: 'normal',
          },
        }}
        headers={[
          { text: t('timeTracker.items.dateOfService'), sx: { width: '10%' }, sortKey: 'dateOfService', },
          { text: t('timeTracker.items.mrn'), sx: { width: '10%' }, sortKey: 'externalId' },
          { text: t('timeTracker.items.patientName'), sx: { width: '20%' }, sortKey: 'patientName' },
          { text: t('timeTracker.items.careTeamMember'), sx: { width: '20%' }, sortKey: 'careTeamMember' },
          { text: t('timeTracker.items.role'), sx: { width: '10%' }, sortKey: 'role' },
          { text: t('timeTracker.items.typeOfService'), sx: { width: '15%' }, sortKey: 'typeOfService' },
          { text: t('timeTracker.items.minutes'), sx: { width: '8%' }, sortKey: 'minutes' },
          { text: t('timeTracker.items.cptCode'), sx: { width: '10%' }, sortKey: 'cptCode' },
          {
            text: t('timeTracker.items.action'),
            sx: { textAlign: 'center', width: '10%' },
          },
        ]}
        items={data.map((item) => getRowData(item))}
      />
    );
  };

  return (
    <>
      <Box sx={{ width: '100%' }} ref={tableRef}>
        {timeTrackers && timeTrackers.length > 0 && (
          <>
            {renderDesktopData(timeTrackers)}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                py: 2,
                paddingBottom: 0,
                pr: matches ? 1 : 20,
                backgroundColor: theme.xPalette.white,
              }}
            >
              <Typography sx={{ fontWeight: 'bold' }}>
                {t('timeTracker.totalMinutes')}: {getNumberInUSFormat(totalMinutes ?? 0)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: showPagingInfo ? 4 : 2,
                borderBottomLeftRadius: '15px',
                borderBottomRightRadius: '15px',
                background: theme.xPalette.white,
              }}
            >
              {showPagingInfo && (
                <CustomPagination
                  changePage={(e, value) => handlePageChange(value)}
                  currentPage={currentPage}
                  pages={totalPages}
                  pageRef={tableRef}
                />
              )}
            </Box>
          </>
        )}
        {pageErrors &&
          pageErrors.length > 0 &&
          pageErrors.map((error) => (
            <Alert key={error} severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ))}
      </Box>
    </>
  );
}
